import { inject, Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { AuthenticationService, LoggingService } from '@lib-ui/lib/services/';
import { localStorageKeys } from '@lib-ui/lib/models/';

@Injectable()
export class AuthorizationInterceptor implements HttpInterceptor {
  private readonly loggingService = inject(LoggingService);
  private readonly b2cAuthenticationService = inject(AuthenticationService);

  /* eslint-disable  @typescript-eslint/no-explicit-any */
  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only intercept API url
    const url = request.url;
    if (!url.includes('/api/')) {
      return next.handle(request);
    }
    // All APIs need JWT authorization
    const headers = {
      Accept: 'application/json',
      Authorization: `Bearer ${this.token}`,
    };

    const newRequest = request.clone({
      url,
      setHeaders: headers,
      withCredentials: true,
    });

    return next.handle(newRequest).pipe(
      mergeMap((event: HttpEvent<any>) => this.handleOkReq(event)),
      catchError(error => this.handleError(error)),
    );
  }

  private handleOkReq(event: HttpEvent<any>): Observable<any> {
    if (event instanceof HttpResponse) {
      if (event && (event.status === 0 || event.status >= 400)) {
        return throwError(new Error(event.statusText));
      }
    }
    return of(event);
  }

  private handleError(error: HttpErrorResponse): Observable<any> {
    let sourceMessage = 'AuthorizationInterceptor:handleError';
    if (error.status === 401) {
      this.b2cAuthenticationService.logout();
      sourceMessage += ' - Logout. Reason:';
    } else if (error.status === 0) {
      sourceMessage += ' - Timeout';
      this.loggingService.logError(sourceMessage, error);
      return new Observable((observer: any) => {
        observer.error(
          'Die Verbindung zum Server wurde unterbrochen. Bitte versuchen sie es erneut.',
        );
        observer.complete();
      });
    }

    this.loggingService.logError(sourceMessage, error);

    const reader: FileReader = new FileReader();

    const observable = new Observable((observer: any) => {
      reader.onloadend = () => {
        const parsedError: string = reader.result ? JSON.parse(<string>reader.result) : '';
        this.loggingService.logError(parsedError);
        observer.error(parsedError);
        observer.complete();
      };
    });
    reader.readAsText(error.error);
    return observable;
  }

  public get token(): string {
    const token = localStorage.getItem(localStorageKeys.ACCESS_TOKEN_KEY);
    return token ? this.b2cAuthenticationService.parseToken(token).accessToken : '';
  }
}
